import { Badge, Button, Checkbox, Select } from '@round/ui-kit';
import { fetchInitialSelectedOptions, useAudioSelect } from './useAudioSelect';
import {
    AudioOption,
    AudioSelectOption,
    AudioSelectOptionSelectedValue,
} from './AudioSelectComponents/AudioSelectComponents';
import { StylesConfig, ValueType } from 'react-select';
import { useCallback, useRef, useState } from 'react';
import { getTiktokAudioReportsFile, getTiktokHashTags } from '@round/api';
import { downloadFile } from '../../../../../utility/utility';
import { showNotification } from '../../../../../helpers';
import styles from './InfluencerReportDownload.module.css';
import { AudioPlayerProvider } from '../../../../AudioPlayer/AudioPlayerContext';
import TiktokAudioBadge from '../../../../TikTok/audios/components/TiktokAudioBadge/TiktokAudioBadge';
import cn from 'classnames';
import CloseIcon from '../../../../../SharedComponents/svg/Icons/CloseIcon';
import useUrlState from '../../../../../Hooks/useUrlState';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import useHashtagSelect, { HashtagOption, mapHashtagsToOption } from './useHashtagSelect';
import useHashtagPaste, { mergeHashtagOptions } from '../useHashtagPaste';

const selectStyles: StylesConfig = {
    valueContainer: (provided) => ({
        ...provided,
        gap: '1rem',
    }),
    control: (provided) => ({
        ...provided,
        fontWeight: 500,
        maxWidth: '22.5rem',
    }),
};

const isValidNumber = (value: string) => Number.isInteger(Number(value)) && Number(value) > 0;

type UrlState = {
    selectedAudios: string;
    excludedAudios: string;
    includeInfluencers: boolean;
    includeUsersWithoutEmail: boolean;
    minFollowerCount: string;
    maxFollowerCount: string;
    selectedHashtags: string;
};

const InfluencerReportDownload = () => {
    const [isDownloading, setIsDownloading] = useState(false);

    const [urlState, setUrlState] = useUrlState<UrlState>();
    const includeInfluencers = urlState.includeInfluencers === 'true';
    const includeUsersWithoutEmail = urlState.includeUsersWithoutEmail === 'true';
    const minFollowerCount = urlState.minFollowerCount ?? '';
    const maxFollowerCount = urlState.maxFollowerCount ?? '';

    const {
        selectProps: audioSelectProps,
        initSelectedOptions: initSelectedAudios,
        isInitialized: isSelectedAudiosInitialized,
    } = useAudioSelect();

    const selectedAudioIds = audioSelectProps.value?.map((audio) => audio.value) ?? [];

    const onChangeSelectedAudios = useCallback(
        (audioOptions: ValueType<AudioOption, true>) => {
            setUrlState({ selectedAudios: audioOptions?.map((option) => option.value).join(',') });
            audioSelectProps.onChange(audioOptions);
        },
        [setUrlState, audioSelectProps]
    );

    const {
        selectProps: excludedAudioSelectProps,
        initSelectedOptions: initExcludedAudios,
        isInitialized: isExcludedAudiosInitialized,
    } = useAudioSelect();

    const excludedAudioIds = excludedAudioSelectProps.value?.map((audio) => audio.value) ?? [];

    const onChangeExcludedAudios = useCallback(
        (audioOptions: ValueType<AudioOption, true>) => {
            setUrlState({ excludedAudios: audioOptions?.map((option) => option.value).join(',') });
            excludedAudioSelectProps.onChange(audioOptions);
        },
        [setUrlState, excludedAudioSelectProps]
    );

    useAbortableEffect(
        (signal) => {
            if (isSelectedAudiosInitialized && isExcludedAudiosInitialized) {
                return;
            }

            const initialSelectedAudios = urlState.selectedAudios?.split(',').map(Number) ?? [];
            const initialExcludedAudios = urlState.excludedAudios?.split(',').map(Number) ?? [];

            if (!urlState.selectedAudios && !urlState.excludedAudios) {
                initSelectedAudios([]);
                initExcludedAudios([]);
                return;
            }
            fetchInitialSelectedOptions([...initialSelectedAudios, ...initialExcludedAudios], { signal }).then(
                (options) => {
                    initSelectedAudios(options?.filter((option) => initialSelectedAudios.includes(option.value)) ?? []);
                    initExcludedAudios(options?.filter((option) => initialExcludedAudios.includes(option.value)) ?? []);
                }
            );
        },
        [
            urlState.selectedAudios,
            urlState.excludedAudios,
            initSelectedAudios,
            initExcludedAudios,
            isSelectedAudiosInitialized,
            isExcludedAudiosInitialized,
        ]
    );

    const {
        selectProps: hashtagsSelectProps,
        initSelectedOptions: initHashtagsSelectedOptions,
        isInitialized: isHashtagsOptionsInitialized,
    } = useHashtagSelect();

    const selectedHashtagIds = hashtagsSelectProps.value?.map((option) => option.value) ?? [];
    const onHashtagsChange = (options: ValueType<HashtagOption, true>) => {
        setUrlState({ selectedHashtags: options?.map((option) => option.value)?.join(',') });
        hashtagsSelectProps.onChange(options);
    };

    // What the lookup merges into, so a tag picked while it was in flight survives the paste.
    const selectedHashtagsRef = useRef(hashtagsSelectProps.value);
    selectedHashtagsRef.current = hashtagsSelectProps.value;

    const { onPaste: onHashtagsPaste, isResolving: areHashtagsResolving } = useHashtagPaste({
        onResolved: (resolved) => onHashtagsChange(mergeHashtagOptions(selectedHashtagsRef.current ?? [], resolved)),
    });

    useAbortableEffect(
        (signal) => {
            const initialHashtags =
                urlState.selectedHashtags
                    ?.split(',')
                    .filter((item) => !!item)
                    .map(Number) ?? [];

            if (isHashtagsOptionsInitialized || !initialHashtags.length) {
                return;
            }

            getTiktokHashTags({ id: initialHashtags.join(','), page_size: initialHashtags.length }, { signal })
                .then((response) => {
                    initHashtagsSelectedOptions(response.data.results.map(mapHashtagsToOption));
                })
                .catch((e) => {
                    if (e instanceof Error && e.name === 'AbortError') {
                        return;
                    }

                    showNotification('Could not initialize hashtags filter', 'error');
                });
        },
        [initHashtagsSelectedOptions, isHashtagsOptionsInitialized, urlState.selectedHashtags]
    );

    const onFileDownload = useCallback(
        async (
            audioIds: number[],
            hashtagIds: number[],
            excludeAudioIds: number[],
            includeInfluencers: boolean,
            minFollowerCount: string,
            maxFollowerCount: string,
            includeUsersWithoutEmail: boolean
        ) => {
            if (!audioIds.length && !hashtagIds.length) {
                return;
            }

            try {
                setIsDownloading(true);
                const response = await getTiktokAudioReportsFile({
                    audio_ids: audioIds.join(','),
                    exclude_audio_ids: excludeAudioIds.join(','),
                    include_influencer_users: includeInfluencers,
                    min_follower_count: isValidNumber(minFollowerCount) ? Number(minFollowerCount) : undefined,
                    max_follower_count: isValidNumber(maxFollowerCount) ? Number(maxFollowerCount) : undefined,
                    include_users_without_email_address: includeUsersWithoutEmail,
                    hashtag_ids: hashtagIds.join(','),
                });

                if (response.status === 200) {
                    downloadFile(response.data);
                    return;
                }

                throw new Error('Error downloading file');
            } catch (e) {
                showNotification((e as Error).message, 'error');
            } finally {
                setIsDownloading(false);
            }
        },
        []
    );

    const handleRemoveAudioSelection = useCallback(
        (audioId: number) => {
            audioSelectProps.onChange(audioSelectProps.value?.filter((audio) => audio.value !== audioId));
            setUrlState({
                selectedAudios: urlState.selectedAudios
                    ?.split(',')
                    .filter((audio) => Number(audio) !== audioId)
                    .join(','),
            });
        },
        [audioSelectProps, setUrlState, urlState.selectedAudios]
    );

    const handleRemoveExcludedAudioSelection = useCallback(
        (audioId: number) => {
            excludedAudioSelectProps.onChange(
                excludedAudioSelectProps.value?.filter((audio) => audio.value !== audioId)
            );
            setUrlState({
                excludedAudios: urlState.excludedAudios
                    ?.split(',')
                    .filter((audio) => Number(audio) !== audioId)
                    .join(','),
            });
        },
        [excludedAudioSelectProps, setUrlState, urlState.excludedAudios]
    );

    const handleRemoveAllAudioSelections = useCallback(() => {
        audioSelectProps.onChange([]);
        setUrlState({ selectedAudios: '' });
    }, [audioSelectProps, setUrlState]);

    const handleRemoveAllExcludedAudioSelections = useCallback(() => {
        excludedAudioSelectProps.onChange([]);
        setUrlState({ excludedAudios: '' });
    }, [excludedAudioSelectProps, setUrlState]);

    const areParametersInvalid =
        (!!minFollowerCount && !isValidNumber(minFollowerCount)) ||
        (!!maxFollowerCount && !isValidNumber(maxFollowerCount));

    return (
        <AudioPlayerProvider>
            <div className={styles.container}>
                <div className={styles.panelsContainer}>
                    <section className={styles.panel}>
                        <div className={styles.row}>
                            <h2 className={styles.title}>Selected Audios</h2>
                            {!!selectedAudioIds.length && (
                                <>
                                    <Badge className={styles.countBadge}>{selectedAudioIds.length}</Badge>
                                    <Button
                                        className={cn(styles.removeButton, styles.black)}
                                        onClick={handleRemoveAllAudioSelections}
                                    >
                                        Clear
                                    </Button>
                                </>
                            )}
                        </div>

                        <Select
                            {...audioSelectProps}
                            onChange={onChangeSelectedAudios}
                            isOptionSelected={(option) =>
                                selectedAudioIds.includes(option.value) || excludedAudioIds.includes(option.value)
                            }
                            controlShouldRenderValue={false}
                            backspaceRemovesValue={false}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            isMulti
                            filterOption={null}
                            placeholder="Search by tiktok id..."
                            components={{
                                Option: AudioSelectOption,
                                MultiValue: AudioSelectOptionSelectedValue,
                                ClearIndicator: () => null,
                            }}
                        />

                        {!selectedAudioIds.length && <span className={styles.empty}>No audios selected</span>}
                        <div className={styles.selectionsContainer}>
                            {audioSelectProps.value?.map((audio) => (
                                <div className={styles.selection} key={audio.value}>
                                    <TiktokAudioBadge
                                        audio={{
                                            tiktokId: audio.tiktokId,
                                            title: audio.title,
                                            authorName: audio.authorName,
                                            isOriginal: audio.isOriginal,
                                            imageUrl: audio.imageUrl,
                                            audioPlayUrl: audio.audioPlayUrl,
                                        }}
                                    />
                                    <Button
                                        className={cn(styles.removeButton, styles.red)}
                                        onClick={() => handleRemoveAudioSelection(audio.value)}
                                    >
                                        <CloseIcon className={styles.removeIcon} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className={cn(styles.panel, styles.bordered)}>
                        <div className={styles.row}>
                            <h2 className={styles.title}>Excluded Audios</h2>
                            {!!excludedAudioIds.length && (
                                <>
                                    <Badge className={styles.countBadge}>{excludedAudioIds.length}</Badge>
                                    <Button
                                        className={cn(styles.removeButton, styles.black)}
                                        onClick={handleRemoveAllExcludedAudioSelections}
                                    >
                                        Clear
                                    </Button>
                                </>
                            )}
                        </div>

                        <Select
                            {...excludedAudioSelectProps}
                            onChange={onChangeExcludedAudios}
                            isOptionSelected={(option) =>
                                selectedAudioIds.includes(option.value) || excludedAudioIds.includes(option.value)
                            }
                            controlShouldRenderValue={false}
                            backspaceRemovesValue={false}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            isMulti
                            filterOption={null}
                            placeholder="Search by tiktok id..."
                            components={{
                                Option: AudioSelectOption,
                                MultiValue: AudioSelectOptionSelectedValue,
                                ClearIndicator: () => null,
                            }}
                        />

                        {!excludedAudioIds.length && <span className={styles.empty}>No audios selected</span>}
                        <div className={styles.selectionsContainer}>
                            {excludedAudioSelectProps.value?.map((audio) => (
                                <div className={styles.selection} key={audio.value}>
                                    <TiktokAudioBadge
                                        audio={{
                                            tiktokId: audio.tiktokId,
                                            title: audio.title,
                                            authorName: audio.authorName,
                                            isOriginal: audio.isOriginal,
                                            imageUrl: audio.imageUrl,
                                            audioPlayUrl: audio.audioPlayUrl,
                                        }}
                                    />
                                    <Button
                                        className={cn(styles.removeButton, styles.red)}
                                        onClick={() => handleRemoveExcludedAudioSelection(audio.value)}
                                    >
                                        <CloseIcon className={styles.removeIcon} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className={cn(styles.panel, styles.bordered)}>
                        <div className={styles.row}>
                            <span className={styles.title}>Selected Hashtags</span>
                            {!!selectedHashtagIds.length && (
                                <>
                                    <Badge className={styles.countBadge}>{selectedHashtagIds.length}</Badge>
                                    <Button
                                        className={cn(styles.removeButton, styles.black)}
                                        onClick={() => {
                                            setUrlState({ selectedHashtags: undefined });
                                            hashtagsSelectProps.onChange([]);
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </>
                            )}
                        </div>

                        {/* Paste lands on the select's own input, and the event reaches this handler on the way up. */}
                        <div onPaste={onHashtagsPaste}>
                            <Select
                                {...hashtagsSelectProps}
                                onChange={onHashtagsChange}
                                isLoading={hashtagsSelectProps.isLoading || areHashtagsResolving}
                                controlShouldRenderValue={false}
                                backspaceRemovesValue={false}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                isMulti
                                filterOption={null}
                                placeholder="Paste or search for hashtags..."
                                components={{
                                    ClearIndicator: () => null,
                                }}
                            />
                        </div>

                        {!selectedHashtagIds?.length && <span className={styles.empty}>No hashtags selected</span>}
                        <div className={styles.selectionsContainer}>
                            {hashtagsSelectProps.value?.map((hashtag) => (
                                <div key={hashtag.value} className={styles.selection}>
                                    <div>{hashtag.label}</div>

                                    <Button
                                        className={cn(styles.removeButton, styles.red)}
                                        onClick={() => {
                                            const newValue = hashtagsSelectProps.value?.filter(
                                                (option) => option.value !== hashtag.value
                                            );
                                            hashtagsSelectProps.onChange(newValue);
                                            setUrlState({ selectedHashtags: newValue?.map((o) => o.value).join(',') });
                                        }}
                                    >
                                        <CloseIcon className={styles.removeIcon} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className={cn(styles.panel, styles.bordered)}>
                        <h2 className={styles.title}>Filters</h2>
                        <div className={styles.column}>
                            <span className={styles.label}>Minimum Follower Count</span>
                            <input
                                type="text"
                                value={minFollowerCount}
                                onChange={(e) => setUrlState({ minFollowerCount: e.currentTarget.value })}
                                placeholder="Enter minimum follower count..."
                                className={styles.filterInput}
                            />
                            {!!minFollowerCount && !isValidNumber(minFollowerCount) && (
                                <span className={styles.error}>Please enter a valid whole number</span>
                            )}
                        </div>

                        <div className={styles.column}>
                            <span className={styles.label}>Maximum Follower Count</span>
                            <input
                                type="text"
                                value={maxFollowerCount}
                                onChange={(e) => setUrlState({ maxFollowerCount: e.currentTarget.value })}
                                placeholder="Enter maximum follower count..."
                                className={styles.filterInput}
                            />
                            {!!maxFollowerCount && !isValidNumber(maxFollowerCount) && (
                                <span className={styles.error}>Please enter a valid whole number</span>
                            )}
                        </div>

                        <div className={styles.row}>
                            <span className={styles.label}>Include Influencer Users</span>
                            <Checkbox
                                value={includeInfluencers}
                                onChange={() =>
                                    setUrlState({ includeInfluencers: includeInfluencers ? undefined : true })
                                }
                            />
                        </div>

                        <div className={styles.row}>
                            <span className={styles.label}>Include Users Without Email Address</span>
                            <Checkbox
                                value={includeUsersWithoutEmail}
                                onChange={() =>
                                    setUrlState({
                                        includeUsersWithoutEmail: includeUsersWithoutEmail ? undefined : true,
                                    })
                                }
                            />
                        </div>

                        <div>
                            <Button
                                type="filled"
                                color="black"
                                disabled={
                                    (!selectedAudioIds?.length && !selectedHashtagIds?.length) || areParametersInvalid
                                }
                                loading={isDownloading}
                                onClick={() =>
                                    onFileDownload(
                                        selectedAudioIds,
                                        selectedHashtagIds,
                                        excludedAudioIds,
                                        includeInfluencers,
                                        minFollowerCount,
                                        maxFollowerCount,
                                        includeUsersWithoutEmail
                                    )
                                }
                            >
                                Download
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
        </AudioPlayerProvider>
    );
};

export default InfluencerReportDownload;
